import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Archive, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ARCHIVE_LEGAL_RECORD, profileArchiveMutations, type ProfileSection } from './queries';
import type { ProfileEntity, ProfileRecord } from './types';

export const ProfileRecordArchive = ({ odrnumber, entity, record, section = 'Legal', onSaved, onClose }: {
  odrnumber: number; entity: ProfileEntity; record: ProfileRecord;
  section?: ProfileSection['key'];
  onSaved: (warnings: string[]) => Promise<unknown>; onClose: () => void;
}) => {
  const [error, setError] = useState<string | null>(null);
  const operation = section === 'Legal' ? 'archiveParticipantProfileLegalRecord' : `archiveParticipantProfile${section}`;
  const [archive, state] = useMutation<Record<string, { success: boolean; warnings?: string[] | null } | null>>(profileArchiveMutations[section] ?? ARCHIVE_LEGAL_RECORD);
  const submit = async () => {
    setError(null);
    try {
      const result = await archive({ variables: { odrnumber, entity: entity.name, key: record.key, revision: record.revision } });
      const saved = result.data?.[operation];
      if (saved?.success !== true) throw new Error('Unable to archive this record.');
      await onSaved(saved.warnings ?? []);
      onClose();
    } catch (error) { setError(error instanceof Error ? error.message : 'Unable to archive this record.'); }
  };
  return <div className="space-y-4">
    <p>Remove this record from the current profile? Its history will be retained.</p>
    {entity.name === 'cases' && <p>Related charges and case hearings will also be archived. Independent hearings will keep their other case references.</p>}
    {error && <p role="alert">{error}</p>}
    <div className="flex justify-end gap-2">
      <Button id={`profile-${entity.name}-archive-confirm`} disabled={state.loading} onClick={() => { void submit(); }}><Archive aria-hidden="true" className="mr-2 h-4 w-4" />Archive record</Button>
      <Button id={`profile-${entity.name}-archive-cancel`} variant="outline" disabled={state.loading} onClick={onClose}><X aria-hidden="true" className="mr-2 h-4 w-4" />Cancel</Button>
    </div>
  </div>;
};
